import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, MessageCircle, Mail, ChevronRight, ChevronDown,
  Tent, Calendar, CreditCard, Clock, CheckCircle,
  AlertCircle, XCircle, FileText, Map, Zap, Upload,
  User, BookOpen, Gift
} from 'lucide-react';
import { Footer } from '../components/Footer';
import { Logo } from '../components/Logo';

// ─── FAQ data ─────────────────────────────────────────────────────────────────
interface FaqEntry { q: string; a: string; isStatusGuide?: boolean }
const FAQS: Record<'exposant' | 'organisateur' | 'paiement' | 'affiliation', FaqEntry[]> = {
  exposant: [
    {
      q: 'Comment créer mon profil exposant ?',
      a: 'Lors de l\'inscription, choisissez le rôle "Exposant". Rendez-vous ensuite dans Mon Stand pour renseigner votre nom commercial, type de stand (food truck, artisanat, mode, vinyles, etc.), région, description, photo de profil et bannière. Plus votre profil est complet, meilleur est votre score de visibilité.',
    },
    {
      q: 'Comment postuler à un festival ?',
      a: 'Sur la page du festival, cliquez sur "Postuler". Votre candidature est envoyée instantanément avec les infos de votre profil. L\'organisateur reçoit une notification. Vous pouvez suivre l\'état depuis Mes Festivals → onglet "En attente".',
    },
    {
      q: 'Que signifient les différents statuts de ma candidature ?',
      a: '',
      isStatusGuide: true,
    },
    {
      q: 'Mon statut est "Paiement requis" — que dois-je faire ?',
      a: 'L\'organisateur a retenu votre candidature ! Il reste une étape : régler les frais de gestion Festin (29€ HT en offre de lancement) via Stripe pour confirmer définitivement votre place. Cliquez sur "Procéder au paiement" dans la carte de candidature. Le paiement est 100% sécurisé.',
    },
    {
      q: 'Comment télécharger mon contrat ?',
      a: 'Après confirmation du paiement, un contrat PDF est généré automatiquement. Il est disponible dans Mes Festivals → onglet "Acceptées" sur la carte de votre candidature confirmée.',
    },
    {
      q: 'Quels documents dois-je uploader sur mon profil ?',
      a: 'Trois documents peuvent renforcer votre profil (optionnels mais recommandés) : Assurance RC Pro, Kbis/SIRET, et Agréments Sanitaires pour les food trucks. Vous pouvez aussi ajouter un Press Kit, menu PDF, ou présentation commerciale.',
    },
    {
      q: 'Comment améliorer mon score et apparaître en premier ?',
      a: 'Votre score dépend de 4 critères : complétion du profil (20%), réputation/notes des organisateurs (40%), fiabilité — taux de présence et paiements à jour (25%), et mise en avant optionnelle (15%). Le boost payant ne peut jamais dépasser un profil mieux noté.',
    },
    {
      q: 'Mes photos ne s\'affichent pas dans Explorer.',
      a: 'Vérifiez que vous avez bien uploadé une photo de profil (logo) depuis la section "Mon Stand". La photo de couverture (bannière) est aussi prise en compte. Si le problème persiste, réuploadez la photo et rafraîchissez la page.',
    },
  ],
  organisateur: [
    {
      q: 'Comment publier mon premier festival ?',
      a: 'La création se fait en 5 étapes guidées : (1) Infos de base — nom, catégorie, dates, organisateur. (2) Localisation — adresse sur la carte. (3) Modèle économique — types de stands et tarifs. (4) Détails — description, identité visuelle, billetterie. (5) Publication — votre festival devient visible aux exposants.',
    },
    {
      q: 'Comment importer mon festival depuis Shotgun ?',
      a: 'À l\'étape 1 de création, collez l\'URL de votre événement Shotgun (ex: https://shotgun.live/fr/events/…). L\'IA extrait automatiquement le nom, les dates, le lieu et la description. Vous n\'avez plus qu\'à vérifier et compléter.',
    },
    {
      q: 'Comment accepter ou refuser une candidature ?',
      a: 'Dans votre tableau de bord festival, allez dans l\'onglet "Candidatures". Chaque fiche exposant affiche son profil complet. Cliquez sur ✓ Retenir ou ✗ Refuser. L\'exposant reçoit une notification instantanée. Si retenu, il doit payer les frais Festin pour confirmer.',
    },
    {
      q: 'Comment faire mon plan de masse ?',
      a: 'Dans le dashboard de votre festival, allez dans l\'onglet "Plan de masse". Vous pouvez : tracer des zones (tentes, scènes, allées), placer des points d\'électricité et d\'eau, puis faire glisser les exposants acceptés vers leur emplacement. L\'IA peut optimiser automatiquement la répartition.',
    },
    {
      q: 'Comment exporter la logistique ?',
      a: 'Depuis le plan de masse, cliquez sur "Exporter la logistique (CSV)". Le fichier contient tous les stands placés avec leurs dimensions, besoins techniques et informations de contact.',
    },
    {
      q: 'Comment générer un contrat pour un exposant ?',
      a: 'Une fois une candidature confirmée (paiement effectué), allez dans la liste des candidatures acceptées et cliquez sur "Générer le contrat". Un PDF est créé avec les infos du stand, les dates, l\'emplacement et le montant. Il est accessible par vous et l\'exposant.',
    },
    {
      q: 'Mon festival est en mode brouillon — comment le publier ?',
      a: 'Depuis Mes Festivals, cliquez sur le festival. Dans le dashboard, un badge orange "Brouillon" apparaît. Complétez les 5 étapes de création, puis publiez. Le statut passe à "Publié" (badge vert) et le festival devient visible dans l\'explorateur.',
    },
    {
      q: 'Les exposants voient-ils mon festival sans compte ?',
      a: 'Non. Seuls les utilisateurs connectés avec le rôle "Exposant" voient les festivals publiés et peuvent postuler. Cela garantit la qualité des candidatures et protège vos informations.',
    },
  ],
  paiement: [
    {
      q: 'Combien coûte Festin pour un organisateur ?',
      a: 'Festin est gratuit pour les organisateurs. Aucune commission, aucun abonnement. Vous recevez 100% du prix de l\'emplacement directement sur votre compte via Stripe.',
    },
    {
      q: 'Combien l\'exposant paie-t-il ?',
      a: 'En offre de lancement : 29€ HT par festival (tarif public : 49€ HT). Si l\'organisateur facture un emplacement supérieur à 164,50€/jour, les frais Festin passent à 30% du montant du stand. Le détail est toujours affiché avant paiement.',
    },
    {
      q: 'Le paiement est-il sécurisé ?',
      a: 'Oui. Festin utilise Stripe, certifié PCI-DSS niveau 1 (le plus élevé). Aucune donnée bancaire ne transite par nos serveurs. Le paiement est sécurisé par chiffrement TLS et validation côté serveur uniquement.',
    },
    {
      q: 'Comment fonctionne le versement à l\'organisateur ?',
      a: 'Stripe Connect split automatiquement le paiement : la part prix du stand va directement sur le compte Stripe Connect de l\'organisateur, les frais Festin restent sur le compte plateforme. Vous devez connecter un compte Stripe depuis les paramètres de votre festival.',
    },
    {
      q: 'Est-ce que je reçois une facture ?',
      a: 'Oui. Un contrat PDF est généré automatiquement après chaque paiement confirmé. Il fait office de reçu et peut servir pour votre comptabilité. Stripe envoie également un reçu par email.',
    },
  ],
  affiliation: [
    {
      q: 'Comment parrainer un autre exposant ?',
      a: 'Depuis le menu (icône avatar en haut à droite), cliquez sur "Parraine ton exposant". Vous obtenez un lien unique à partager. Quand un exposant s\'inscrit via ce lien, il bénéficie de 10€ de réduction sur ses frais Festin, et vous recevez un crédit sur votre compte.',
    },
    {
      q: 'Combien je gagne en parrainant des exposants ?',
      a: 'Chaque filleul qui confirme une candidature vous rapporte un crédit, dans la limite de 50€ cumulés. Le crédit s\'applique automatiquement sur vos propres frais Festin lors de votre prochain paiement.',
    },
    {
      q: 'Mon filleul ne voit pas la réduction de 10€, pourquoi ?',
      a: 'La réduction s\'applique uniquement si l\'inscription se fait via votre lien de parrainage (badge "Invité par un exposant Festin" visible sur l\'écran d\'inscription). Si votre filleul a créé son compte sans passer par le lien, le parrainage ne peut pas être rattaché rétroactivement.',
    },
    {
      q: 'Qu\'est-ce que le programme Promoteur ?',
      a: 'Le programme Promoteur s\'adresse à toute personne qui met Festin en relation avec des organisateurs de festivals (pas seulement des exposants). En soumettant un festival qui s\'inscrit et publie sur la plateforme, vous touchez une commission de 20% sur les frais Festin générés par ce festival.',
    },
    {
      q: 'Comment devenir Promoteur ?',
      a: 'Depuis le menu, cliquez sur "Devenir Promoteur", remplissez le formulaire d\'inscription puis signez les CGV. Votre compte est ensuite validé manuellement par l\'équipe Festin avant activation.',
    },
    {
      q: 'Comment et quand suis-je payé en tant que Promoteur ?',
      a: 'Les commissions sont calculées automatiquement sur chaque paiement confirmé lié à un festival que vous avez soumis. Le versement se fait depuis votre tableau de bord Promoteur (bouton de versement), avec un récapitulatif mensuel des commissions en attente.',
    },
  ],
};

// ─── Status guide ──────────────────────────────────────────────────────────────
const STATUSES = [
  {
    icon: <Clock className="w-4 h-4" />,
    color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    label: 'En attente',
    desc: 'Candidature envoyée, l\'organisateur n\'a pas encore répondu.',
  },
  {
    icon: <AlertCircle className="w-4 h-4" />,
    color: 'text-[#FF5C00] bg-[#FF5C00]/10 border-[#FF5C00]/20',
    label: 'Paiement requis',
    desc: 'Félicitations, vous êtes retenu(e) ! Payez les frais Festin pour confirmer votre place.',
  },
  {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
    label: 'Confirmé',
    desc: 'Paiement validé. Votre emplacement est officiellement réservé. Contrat disponible.',
  },
  {
    icon: <XCircle className="w-4 h-4" />,
    color: 'text-red-400 bg-red-400/10 border-red-400/20',
    label: 'Refusé',
    desc: 'L\'organisateur n\'a pas retenu votre candidature pour cette édition.',
  },
];

// ─── Articles ──────────────────────────────────────────────────────────────────
const ARTICLES = [
  { title: 'Comprendre les statuts de candidature', category: 'Exposants', slug: 'comprendre-statuts', icon: <Clock className="w-4 h-4 text-yellow-400" /> },
  { title: 'Compléter son profil exposant à 100%', category: 'Exposants', slug: 'profil-exposant-complet', icon: <User className="w-4 h-4 text-[#FF5C00]" /> },
  { title: 'Créer et publier son festival en 5 étapes', category: 'Organisateurs', slug: 'publier-festival', icon: <Calendar className="w-4 h-4 text-[#00A3FF]" /> },
  { title: 'Utiliser le plan de masse', category: 'Organisateurs', slug: 'plan-de-masse', icon: <Map className="w-4 h-4 text-[#00A3FF]" /> },
  { title: 'Importer un festival depuis Shotgun', category: 'Organisateurs', slug: 'import-shotgun', icon: <Upload className="w-4 h-4 text-[#00A3FF]" /> },
  { title: 'Tarification et frais Festin', category: 'Paiement', slug: 'tarification-festin', icon: <CreditCard className="w-4 h-4 text-purple-400" /> },
  { title: 'L\'installation électrique en festival', category: 'Logistique', slug: 'guide-electrique-festival', icon: <Zap className="w-4 h-4 text-[#FF5C00]" /> },
  { title: 'Checklist avant le jour J', category: 'Préparation', slug: 'checklist-jour-j', icon: <FileText className="w-4 h-4 text-gray-400" /> },
];

// ─── FAQ accordion item ────────────────────────────────────────────────────────
function FaqItem({ q, a, isStatusGuide }: { q: string; a: string; isStatusGuide?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/5 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left gap-4 group"
      >
        <span className="font-medium text-gray-200 group-hover:text-white transition-colors">{q}</span>
        <ChevronDown className={`w-4 h-4 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="pb-5">
          {isStatusGuide ? (
            <div className="space-y-2">
              {STATUSES.map(s => (
                <div key={s.label} className={`flex items-start gap-3 border rounded-xl px-4 py-3 ${s.color}`}>
                  <span className="mt-0.5 shrink-0">{s.icon}</span>
                  <div>
                    <span className="font-bold text-sm">{s.label}</span>
                    <p className="text-sm opacity-80 mt-0.5">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 leading-relaxed">{a}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function SupportPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'exposant' | 'organisateur' | 'paiement' | 'affiliation'>('exposant');

  const currentFaqs = FAQS[activeTab].filter(f =>
    !searchQuery || f.q.toLowerCase().includes(searchQuery.toLowerCase()) || f.a.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allFaqsFiltered = searchQuery
    ? [...FAQS.exposant, ...FAQS.organisateur, ...FAQS.paiement, ...FAQS.affiliation].filter(
        f => f.q.toLowerCase().includes(searchQuery.toLowerCase()) || f.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : null;

  return (
    <div className="min-h-screen bg-[#121212] text-white selection:bg-[#FF5C00]/30 font-sans">

      {/* Hero */}
      <div className="relative py-20 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#00A3FF]/10 via-transparent to-[#FF5C00]/10" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-[#FF5C00] opacity-15 blur-[150px] rounded-full pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <button onClick={() => navigate('/')} className="inline-block mb-8">
            <Logo />
          </button>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-5">
            Centre d'aide
          </h1>
          <p className="text-lg text-gray-400 mb-8">Trouvez une réponse ou contactez l'équipe.</p>

          <div className="relative max-w-2xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-[#FF5C00]" />
            </div>
            <input
              type="text"
              className="block w-full pl-14 pr-5 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF5C00] focus:bg-white/10 transition-all font-medium backdrop-blur-xl shadow-2xl"
              placeholder="Rechercher 'paiement', 'statut', 'plan de masse'…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-16">

        {/* Quick shortcuts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-16">
          {[
            { icon: <Tent className="w-5 h-5 text-[#FF5C00]" />, label: 'Mon profil exposant', action: () => navigate('/my-stand') },
            { icon: <Calendar className="w-5 h-5 text-[#00A3FF]" />, label: 'Mes festivals', action: () => navigate('/my-festivals') },
            { icon: <CreditCard className="w-5 h-5 text-purple-400" />, label: 'Tarification', action: () => navigate('/tarification') },
            { icon: <Mail className="w-5 h-5 text-gray-400" />, label: 'Nous contacter', action: () => navigate('/contact') },
          ].map(({ icon, label, action }) => (
            <button
              key={label}
              onClick={action}
              className="flex items-center gap-3 bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/15 rounded-2xl px-4 py-3 transition-all text-left group"
            >
              {icon}
              <span className="text-sm font-medium text-gray-300 group-hover:text-white transition-colors leading-tight">{label}</span>
            </button>
          ))}
        </div>

        {/* FAQ section */}
        <div className="mb-20">
          <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-white/40" />
            Questions fréquentes
          </h2>

          {/* Tab selector */}
          {!searchQuery && (
            <div className="flex gap-2 mb-6 flex-wrap">
              {([
                { id: 'exposant', icon: <Tent className="w-3.5 h-3.5" />, label: 'Exposants' },
                { id: 'organisateur', icon: <Calendar className="w-3.5 h-3.5" />, label: 'Organisateurs' },
                { id: 'paiement', icon: <CreditCard className="w-3.5 h-3.5" />, label: 'Paiement' },
                { id: 'affiliation', icon: <Gift className="w-3.5 h-3.5" />, label: 'Parrainage & Promoteur' },
              ] as const).map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-[#FF5C00] text-white'
                      : 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 border border-white/5'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          <div className="bg-[#1A1A1A] border border-white/5 rounded-3xl px-6 py-2">
            {(allFaqsFiltered || currentFaqs).length === 0 ? (
              <p className="py-8 text-center text-gray-500 text-sm">Aucun résultat pour "{searchQuery}"</p>
            ) : (
              (allFaqsFiltered || currentFaqs).map((faq, i) => (
                <FaqItem key={i} q={faq.q} a={faq.a} isStatusGuide={faq.isStatusGuide} />
              ))
            )}
          </div>
        </div>

        {/* Articles */}
        <div className="mb-20">
          <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
            <FileText className="w-5 h-5 text-white/40" />
            Guides détaillés
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ARTICLES.map((article) => (
              <button
                key={article.slug}
                onClick={() => navigate(`/support/article/${article.slug}`)}
                className="flex items-center gap-4 bg-white/3 hover:bg-white/8 border border-white/5 hover:border-white/15 p-5 rounded-2xl transition-all group text-left"
              >
                <div className="w-9 h-9 bg-white/5 rounded-xl flex items-center justify-center shrink-0">
                  {article.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-200 group-hover:text-white transition-colors text-sm leading-tight">{article.title}</p>
                  <p className="text-xs text-gray-500 mt-1">{article.category}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-gray-400 transition-colors shrink-0" />
              </button>
            ))}
          </div>
        </div>

        {/* Contact */}
        <div>
          <h2 className="text-center text-gray-400 font-medium mb-6">Vous n'avez pas trouvé ? On est là.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-4 bg-[#1A1A1A] border border-white/10 p-5 rounded-2xl hover:border-white/30 transition-colors group"
            >
              <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <Mail className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-bold text-sm">Envoyer un message</p>
                <p className="text-xs text-gray-500 mt-0.5">contact@festin.live</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/messages')}
              className="flex items-center gap-4 bg-[#1A1A1A] border border-white/10 p-5 rounded-2xl hover:border-white/30 transition-colors group"
            >
              <div className="w-10 h-10 bg-white/5 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="font-bold text-sm">Chat en direct</p>
                <p className="text-xs text-[#00A3FF] mt-0.5">Réponse en moins de 10 min</p>
              </div>
            </button>
          </div>
        </div>

        {/* Status */}
        <div className="mt-16 flex items-center justify-center space-x-2 text-sm text-gray-500 bg-white/3 w-max mx-auto px-4 py-2 rounded-full border border-white/5">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>Tous les systèmes sont opérationnels.</span>
        </div>
      </main>

      <Footer />
    </div>
  );
}
